<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { messageStamp } from 'shared/helpers/timeHelper';
import { formatReportDuration } from '../constants';
import BarChart from 'shared/components/charts/BarChart.vue';
import AgentActivityTimeline from './AgentActivityTimeline.vue';

const props = defineProps({
  agents: { type: Array, default: () => [] },
  onlineByDay: { type: Array, default: () => [] },
  statusComposition: { type: Object, default: () => ({}) },
  from: { type: Number, default: 0 },
  to: { type: Number, default: 0 },
  selectedDay: { type: String, default: null },
});

const emit = defineEmits(['dayClick', 'openDetail']);
const { t } = useI18n();

const HOUR_SECONDS = 60 * 60;
const DAY_SECONDS = 24 * HOUR_SECONDS;
const WEEK_SECONDS = 7 * DAY_SECONDS;
const MONTH_SECONDS_APPROX = 30 * DAY_SECONDS;
const TARGET_TICKS = 6;

const viewFrom = ref(0);
const viewTo = ref(0);
const selectedScale = ref('daily');

const SCALE_TO_SECONDS = {
  hourly: DAY_SECONDS,
  daily: 14 * DAY_SECONDS,
  weekly: 8 * WEEK_SECONDS,
  monthly: 6 * MONTH_SECONDS_APPROX,
};

const availableScales = computed(() => [
  { key: 'hourly', label: t('AGENT_ACTIVITY_REPORT.SCALE.HOURLY') },
  { key: 'daily', label: t('AGENT_ACTIVITY_REPORT.SCALE.DAILY') },
  { key: 'weekly', label: t('AGENT_ACTIVITY_REPORT.SCALE.WEEKLY') },
  { key: 'monthly', label: t('AGENT_ACTIVITY_REPORT.SCALE.MONTHLY') },
]);

const toDate = value => new Date(Number(value || 0) * 1000);
const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const startOfHour = value => {
  const d = toDate(value);
  return Math.floor(
    Date.UTC(
      d.getUTCFullYear(),
      d.getUTCMonth(),
      d.getUTCDate(),
      d.getUTCHours()
    ) / 1000
  );
};
const startOfDay = value => {
  const d = toDate(value);
  return Math.floor(
    Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()) / 1000
  );
};
const startOfMonth = value => {
  const d = toDate(value);
  return Math.floor(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1) / 1000);
};
const addMonths = (timestamp, count) => {
  const d = toDate(timestamp);
  return Math.floor(
    Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + count, 1) / 1000
  );
};

const periodSeconds = computed(() =>
  Math.max(0, Number(props.to || 0) - Number(props.from || 0))
);

const visibleWindowSeconds = computed(() => {
  const full = Math.max(1, periodSeconds.value);
  return Math.min(SCALE_TO_SECONDS[selectedScale.value] || full, full);
});

const canMovePrevious = computed(
  () => viewFrom.value > Number(props.from || 0)
);
const canMoveNext = computed(() => viewTo.value < Number(props.to || 0));

const syncViewport = ({ anchorToEnd = true } = {}) => {
  const f = Number(props.from || 0);
  const end = Number(props.to || 0);
  if (!f || !end || end <= f) {
    viewFrom.value = f;
    viewTo.value = end;
    return;
  }
  const range = end - f;
  const window = Math.min(visibleWindowSeconds.value, range);
  const baseEnd = anchorToEnd
    ? end
    : clamp(viewTo.value || end, f + window, end);
  viewTo.value = baseEnd;
  viewFrom.value = Math.max(f, baseEnd - window);
};

const shiftViewport = direction => {
  const span = Math.max(1, viewTo.value - viewFrom.value);
  const moveBy = Math.max(1, Math.floor(span / 2));
  const f = Number(props.from || 0);
  const end = Number(props.to || 0);
  const targetFrom = clamp(viewFrom.value + direction * moveBy, f, end - span);
  viewFrom.value = targetFrom;
  viewTo.value = targetFrom + span;
};

const resetViewport = () => syncViewport({ anchorToEnd: true });

// TZ §9: periods longer than 7 days collapse timeline to daily bars by default.
const pickScaleForPeriod = duration => {
  if (duration <= 7 * DAY_SECONDS) return 'hourly';
  if (duration <= 62 * DAY_SECONDS) return 'daily';
  if (duration <= 370 * DAY_SECONDS) return 'weekly';
  return 'monthly';
};

const zoomTimelineToDay = dateStr => {
  if (!dateStr) return;
  const dayStart = Math.floor(Date.parse(`${dateStr}T00:00:00.000Z`) / 1000);
  if (!Number.isFinite(dayStart)) return;
  const dayEnd = dayStart + DAY_SECONDS;
  const periodFrom = Number(props.from || 0);
  const periodTo = Number(props.to || 0);
  selectedScale.value = 'hourly';
  viewFrom.value = Math.max(periodFrom, dayStart);
  viewTo.value = Math.min(periodTo || dayEnd, dayEnd);
};

const onScaleChange = value => {
  selectedScale.value = value;
  syncViewport({ anchorToEnd: false });
};

watch(
  () => [props.from, props.to],
  () => {
    selectedScale.value = pickScaleForPeriod(periodSeconds.value);
    syncViewport({ anchorToEnd: true });
  },
  { immediate: true }
);

const pickStep = (target, candidates) =>
  candidates.find(c => c >= target) || candidates[candidates.length - 1];

const timelineResolution = computed(() => {
  if (
    selectedScale.value === 'hourly' ||
    periodSeconds.value <= 7 * DAY_SECONDS
  ) {
    return 'hourly';
  }
  if (selectedScale.value === 'daily') return 'daily';
  if (selectedScale.value === 'weekly') return 'weekly';
  if (selectedScale.value === 'monthly') return 'monthly';

  const duration = Math.max(
    1,
    Number(viewTo.value || 0) - Number(viewFrom.value || 0)
  );
  if (duration <= 7 * DAY_SECONDS) return 'hourly';
  if (duration <= 62 * DAY_SECONDS) return 'daily';
  if (duration <= 370 * DAY_SECONDS) return 'weekly';
  return 'monthly';
});

const clipFrom = computed(() =>
  Math.max(Number(props.from || 0), Number(viewFrom.value || 0))
);
const clipTo = computed(() =>
  Math.min(Number(props.to || 0), Number(viewTo.value || 0))
);

const timelineTicks = computed(() => {
  const start = clipFrom.value;
  const end = clipTo.value;
  if (!start || !end || end <= start) return [];
  const span = Math.max(1, end - start);

  const tickLabel = value => {
    if (timelineResolution.value === 'hourly') {
      return messageStamp(value, 'HH:mm');
    }
    if (timelineResolution.value === 'monthly') {
      return messageStamp(value, 'MMM yyyy');
    }
    return messageStamp(value, 'MMM d');
  };

  const toTick = value => ({
    value,
    x: ((value - start) / span) * 100,
    label: tickLabel(value),
  });

  if (timelineResolution.value === 'monthly') {
    const monthSpan = Math.max(1, span / MONTH_SECONDS_APPROX);
    const stepMonths = pickStep(monthSpan / TARGET_TICKS, [1, 2, 3, 6, 12]);
    const values = [];
    let cursor = startOfMonth(start);
    while (cursor <= end) {
      if (cursor >= start) values.push(cursor);
      cursor = addMonths(cursor, stepMonths);
    }
    if (!values.length || values[values.length - 1] !== end) values.push(end);
    return values.map(toTick);
  }

  let baseStep;
  if (timelineResolution.value === 'hourly') {
    baseStep = pickStep(span / TARGET_TICKS, [
      HOUR_SECONDS,
      2 * HOUR_SECONDS,
      3 * HOUR_SECONDS,
      4 * HOUR_SECONDS,
      6 * HOUR_SECONDS,
      12 * HOUR_SECONDS,
    ]);
  } else if (timelineResolution.value === 'daily') {
    baseStep = pickStep(span / TARGET_TICKS, [
      DAY_SECONDS,
      2 * DAY_SECONDS,
      3 * DAY_SECONDS,
      7 * DAY_SECONDS,
      14 * DAY_SECONDS,
    ]);
  } else {
    baseStep = pickStep(span / TARGET_TICKS, [
      WEEK_SECONDS,
      2 * WEEK_SECONDS,
      4 * WEEK_SECONDS,
      8 * WEEK_SECONDS,
    ]);
  }

  const values = [];
  let cursor =
    timelineResolution.value === 'hourly'
      ? startOfHour(start)
      : startOfDay(start);
  while (cursor <= end) {
    if (cursor >= start) values.push(cursor);
    cursor += baseStep;
  }
  if (!values.length || values[values.length - 1] !== end) values.push(end);
  return values.map(toTick);
});

const clipSegments = (segments, start, end) =>
  (segments || [])
    .map(segment => {
      const from = Math.max(Number(segment.from || 0), start);
      const to = Math.min(Number(segment.to || 0), end);
      return { ...segment, from, to, duration: Math.max(0, to - from) };
    })
    .filter(segment => segment.duration > 0);

const agentRows = computed(() =>
  (props.agents || []).map(agent => {
    const segments = clipSegments(agent.segments, clipFrom.value, clipTo.value);
    const totals = { accepting: 0, not_accepting: 0, logged_out: 0 };
    segments.forEach(segment => {
      if (segment.availability === 'online')
        totals.accepting += segment.duration;
      else if (segment.availability === 'busy')
        totals.not_accepting += segment.duration;
      else totals.logged_out += segment.duration;
    });
    return { ...agent, displaySegments: segments, displayTotals: totals };
  })
);

const onlineByDayCollection = computed(() => ({
  labels: (props.onlineByDay || []).map(row => row.date),
  datasets: [
    {
      label: t('AGENT_ACTIVITY_REPORT.STATUSES.ACCEPTING'),
      data: (props.onlineByDay || []).map(row => row.accepting || 0),
      backgroundColor: '#22c55e',
      stack: 'online',
    },
    {
      label: t('AGENT_ACTIVITY_REPORT.STATUSES.NOT_ACCEPTING'),
      data: (props.onlineByDay || []).map(row => row.not_accepting || 0),
      backgroundColor: '#eab308',
      stack: 'online',
    },
  ],
}));

const compositionCollection = computed(() => ({
  labels: [t('AGENT_ACTIVITY_REPORT.CHARTS.STATUS_COMPOSITION')],
  datasets: [
    {
      label: t('AGENT_ACTIVITY_REPORT.STATUSES.ACCEPTING'),
      data: [props.statusComposition.accepting || 0],
      backgroundColor: '#22c55e',
      stack: 'composition',
    },
    {
      label: t('AGENT_ACTIVITY_REPORT.STATUSES.NOT_ACCEPTING'),
      data: [props.statusComposition.not_accepting || 0],
      backgroundColor: '#eab308',
      stack: 'composition',
    },
    {
      label: t('AGENT_ACTIVITY_REPORT.STATUSES.OFFLINE'),
      data: [props.statusComposition.offline || 0],
      backgroundColor: '#64748b',
      stack: 'composition',
    },
  ],
}));

const stackedOptions = {
  scales: {
    x: { stacked: true, grid: { drawOnChartArea: false } },
    y: {
      stacked: true,
      beginAtZero: true,
      grid: { drawOnChartArea: false },
      ticks: {
        callback: value => formatReportDuration(value),
      },
    },
  },
  plugins: {
    tooltip: {
      callbacks: {
        label: context =>
          `${context.dataset.label}: ${formatReportDuration(context.parsed.y)}`,
      },
    },
  },
};

const resetTimelineViewport = () => {
  selectedScale.value = pickScaleForPeriod(periodSeconds.value);
  syncViewport({ anchorToEnd: true });
};

const onOnlineDayBarClick = event => {
  const date = (props.onlineByDay || [])[event.index]?.date;
  if (!date) return;
  const deselecting = props.selectedDay === date;
  emit('dayClick', date);
  if (deselecting) resetTimelineViewport();
  else zoomTimelineToDay(date);
};

watch(
  () => props.selectedDay,
  day => {
    if (day) zoomTimelineToDay(day);
    else resetTimelineViewport();
  }
);
</script>

<template>
  <div class="mt-4 flex flex-col gap-4">
    <div class="grid gap-4 lg:grid-cols-2">
      <section
        class="px-6 py-5 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2"
      >
        <h3 class="text-base font-medium text-n-slate-12">
          {{ $t('AGENT_ACTIVITY_REPORT.CHARTS.ONLINE_BY_DAY') }}
        </h3>
        <div class="mt-4 h-72">
          <BarChart
            :collection="onlineByDayCollection"
            show-legend
            :chart-options="stackedOptions"
            @bar-click="onOnlineDayBarClick"
          />
        </div>
        <p v-if="selectedDay" class="mt-2 text-xs text-n-slate-11">
          {{
            $t('AGENT_ACTIVITY_REPORT.SUMMARY.FILTERED_DAY', {
              date: selectedDay,
            })
          }}
        </p>
      </section>

      <section
        class="px-6 py-5 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2"
      >
        <h3 class="text-base font-medium text-n-slate-12">
          {{ $t('AGENT_ACTIVITY_REPORT.CHARTS.STATUS_COMPOSITION') }}
        </h3>
        <div class="mt-4 h-72">
          <BarChart
            :collection="compositionCollection"
            show-legend
            :chart-options="stackedOptions"
          />
        </div>
        <div
          class="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-n-slate-11"
        >
          <span class="whitespace-nowrap">
            {{ $t('AGENT_ACTIVITY_REPORT.STATUSES.ACCEPTING') }}:
            {{ formatReportDuration(statusComposition.accepting) }}
          </span>
          <span class="whitespace-nowrap">
            {{ $t('AGENT_ACTIVITY_REPORT.STATUSES.NOT_ACCEPTING') }}:
            {{ formatReportDuration(statusComposition.not_accepting) }}
          </span>
          <span class="whitespace-nowrap">
            {{ $t('AGENT_ACTIVITY_REPORT.STATUSES.OFFLINE') }}:
            {{ formatReportDuration(statusComposition.offline) }}
          </span>
        </div>
      </section>
    </div>

    <section class="flex flex-col gap-3">
      <h3 class="px-1 text-base font-medium text-n-slate-12">
        {{ $t('AGENT_ACTIVITY_REPORT.CHARTS.TIMELINE') }}
      </h3>

      <div
        v-for="row in agentRows"
        :key="row.user_id"
        class="rounded-xl border border-n-weak bg-n-solid-2 p-4"
      >
        <div class="mb-4 flex items-center justify-between gap-3">
          <div class="min-w-0">
            <p class="mb-0 truncate text-sm font-medium text-n-slate-12">
              {{ row.agent_name }}
            </p>
            <p class="mb-0 truncate text-xs text-n-slate-11">{{ row.email }}</p>
          </div>
          <button
            type="button"
            class="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-lg border border-n-weak px-3 text-xs text-n-slate-11 transition hover:border-n-brand hover:text-n-brand"
            @click="emit('openDetail', row.user_id)"
          >
            {{ $t('AGENT_ACTIVITY_REPORT.DETAILS_BUTTON') }}
            <span class="i-lucide-arrow-up-right size-3 opacity-60" />
          </button>
        </div>

        <AgentActivityTimeline
          :segments="row.displaySegments"
          :timeline-start="clipFrom"
          :timeline-end="clipTo"
          :timeline-ticks="timelineTicks"
          :user-id="row.user_id"
          :can-move-previous="canMovePrevious"
          :can-move-next="canMoveNext"
          :selected-scale="selectedScale"
          :available-scales="availableScales"
          :display-totals="row.displayTotals"
          @previous="shiftViewport(-1)"
          @next="shiftViewport(1)"
          @reset="resetViewport"
          @scale-change="onScaleChange"
        />
      </div>

      <p
        v-if="!agents.length"
        class="rounded-xl border border-n-weak bg-n-alpha-2 px-4 py-10 text-center text-sm text-n-slate-11"
      >
        {{ $t('AGENT_ACTIVITY_REPORT.EMPTY') }}
      </p>
    </section>
  </div>
</template>
